'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Users,
  Calendar,
  Image as ImageIcon,
  Loader2,
  Search,
  X,
  Plane
} from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { getHybridRootAlbums, getHybridSubAlbums, HybridAlbum } from '@/lib/gallery-hybrid';
import { Button } from '@/components/ui/button';
import { getHierarchyCache, HierarchyNode } from '@/lib/gallery-cache';

// Extended album type for tree structure
interface TreeAlbum extends HybridAlbum {
  children?: TreeAlbum[];
}

export interface AlbumTreeNavigatorProps {
  /** Current selected album ID */
  selectedAlbumId?: string;
  /** Callback when album selected */
  onAlbumSelect?: (album: HybridAlbum) => void;
  /** Maximum depth to auto-expand (default: 1) */
  autoExpandDepth?: number;
  /** Show media counts */
  showMediaCounts?: boolean;
  /** Use links for navigation */
  useLinks?: boolean;
  /** Base URL for links (default: /gallery) */
  baseLinkUrl?: string;
  /** Class name for container */
  className?: string;
  /** Change this value to trigger a refresh of the tree */
  refreshKey?: number;
}

/**
 * Hierarchical album tree navigator with expandable nodes.
 * Displays albums in a tree structure.
 * PREFERS cached hierarchy for instant load. FALLBACKS to iterative fetch.
 */
export function AlbumTreeNavigator({
  selectedAlbumId,
  onAlbumSelect,
  autoExpandDepth = 1,
  showMediaCounts = true,
  useLinks = true,
  baseLinkUrl = '/gallery',
  className,
  refreshKey,
}: AlbumTreeNavigatorProps) {
  const firestore = useFirestore();
  const auth = useAuth();

  const [rootAlbums, setRootAlbums] = React.useState<TreeAlbum[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Flattened list for search
  const allAlbums = React.useMemo(() => {
    const flatten = (nodes: TreeAlbum[]): TreeAlbum[] => {
      return nodes.reduce((acc, node) => {
        return [...acc, node, ...flatten(node.children || [])];
      }, [] as TreeAlbum[]);
    };
    return flatten(rootAlbums);
  }, [rootAlbums]);

  React.useEffect(() => {
    if (!firestore) return;

    let mounted = true;

    async function loadData() {
      if (!mounted) return;
      setLoading(true);
      setError(null);

      try {
        // 1. Try Cache First
        const role = auth.currentUser ? 'members' : 'public';
        console.log(`[AlbumTree] Loading hierarchy for role: ${role}`);
        let cache: HierarchyNode[] | null = null;

        try {
          cache = await getHierarchyCache(firestore!, role);
        } catch (e: any) {
          // If we get a permission denied error for 'members' role, it means the user is authenticated
          // but not an active member. Fallback to 'public' role immediately.
          if (role === 'members' && e?.code === 'permission-denied') {
            console.warn("Permission denied for member cache, falling back to public cache");
            cache = await getHierarchyCache(firestore!, 'public');
          } else {
            throw e;
          }
        }

        if (cache && cache.length > 0) {
          // Convert HierarchyNode -> TreeAlbum
          const mapNode = (node: HierarchyNode, depth: number = 0, parentPath: string = ''): TreeAlbum => {
            const slug = node.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            const currentPath = parentPath ? `${parentPath}/${slug}` : slug;
            const children = node.children?.map(child => mapNode(child, depth + 1, currentPath)) || [];

            return {
              id: node.id,
              name: node.name,
              source: node.source,
              // photonlinkId removed
              albumType: (node.albumType as any) || 'custom',
              description: node.description,
              visibility: (node.visibility as any) || 'public',
              mediaCount: node.mediaCount || 0,
              children,
              coverUrl: node.coverUrl,
              depth,
              path: currentPath
            };
          };

          if (mounted) {
            setRootAlbums(cache.map(n => mapNode(n, 0)));
            setLoading(false);
          }
          return;
        }

        // 2. Cache Miss -> Trigger Sync & Fallback to lazy load
        console.log("Hierarchy cache miss, falling back to iterative load & triggering sync");

        // Trigger Sync (Fire & Forget)
        const token = auth.currentUser ? await auth.currentUser.getIdToken().catch(() => undefined) : undefined;
        fetch('/api/gallery/sync-hierarchy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ role })
        }).catch(e => console.warn("Background sync trigger failed", e));

        // Fallback: Load Roots only (Sub-albums will lazy load)
        const roots = await getHybridRootAlbums(firestore!);
        if (mounted) {
          setRootAlbums(roots); // roots don't have children property set yet, rendered items will fetch
        }

      } catch (err) {
        console.error("Error loading album tree:", err);
        if (mounted) setError(err instanceof Error ? err : new Error('Failed to load'));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => { mounted = false; };
  }, [firestore, auth.currentUser, refreshKey]); // Re-run if user logs in/out or refreshKey changes

  // Filter albums based on search query
  const filteredAlbums = React.useMemo(() => {
    if (!searchQuery.trim()) return rootAlbums;

    const query = searchQuery.toLowerCase();
    // Search flat list
    return allAlbums.filter(album =>
      album.name.toLowerCase().includes(query) ||
      album.description?.toLowerCase().includes(query)
    );
  }, [rootAlbums, allAlbums, searchQuery]);

  if (loading && rootAlbums.length === 0) {
    return <AlbumTreeSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Error loading albums: {error.message}
      </div>
    );
  }

  if (!rootAlbums || rootAlbums.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No albums found.
      </div>
    );
  }

  return (
    <div className={cn('album-tree-container', className)}>
      {/* Search input */}
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search albums..."
          className="pl-8 pr-8 h-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1 h-7 w-7"
            onClick={() => setSearchQuery('')}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Album tree */}
      {filteredAlbums.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground text-center">
          No albums match your search.
        </div>
      ) : (
        <nav>
          {filteredAlbums.map((album) => (
            <AlbumTreeNode
              key={album.id}
              album={album}
              depth={0}
              autoExpandDepth={searchQuery ? 0 : autoExpandDepth}
              selectedAlbumId={selectedAlbumId}
              onAlbumSelect={onAlbumSelect}
              showMediaCounts={showMediaCounts}
              useLinks={useLinks}
              baseLinkUrl={baseLinkUrl}
              searchMode={searchQuery.trim().length > 0}
            />
          ))}
        </nav>
      )}
    </div>
  );
}

interface AlbumTreeNodeProps {
  album: TreeAlbum;
  depth: number;
  autoExpandDepth: number;
  selectedAlbumId?: string;
  onAlbumSelect?: (album: HybridAlbum) => void;
  showMediaCounts: boolean;
  useLinks: boolean;
  baseLinkUrl: string;
  searchMode?: boolean;
}

function AlbumTreeNode({
  album,
  depth,
  autoExpandDepth,
  selectedAlbumId,
  onAlbumSelect,
  showMediaCounts,
  useLinks,
  baseLinkUrl,
  searchMode = false,
}: AlbumTreeNodeProps) {
  const firestore = useFirestore();
  const [expanded, setExpanded] = React.useState(depth < autoExpandDepth);

  // Use pre-loaded children if available (from cache), otherwise render empty (and fetch if needed)
  // If we utilize the cache, 'album.children' is already populated.
  // If we are in fallback mode (root albums only), 'children' is undefined.

  const [fallbackSubAlbums, setFallbackSubAlbums] = React.useState<HybridAlbum[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Computed children: either from props (cache) or state (fallback fetch)
  const effectiveChildren = album.children || fallbackSubAlbums;
  const hasMinChildren = effectiveChildren.length > 0;

  const isSelected = album.id === selectedAlbumId;

  // Fallback Fetch Logic: Only runs if NO children provided AND expanded
  React.useEffect(() => {
    // If we already have children from cache, do nothing
    if (album.children && album.children.length > 0) return;

    if (!firestore || !expanded || searchMode) return;

    // Check if we already fetched fallback
    if (fallbackSubAlbums.length > 0) return;

    let mounted = true;
    async function fetchSubAlbums() {
      try {
        setLoading(true);
        // source is always firebase now, no photonlinkId
        const albums = await getHybridSubAlbums(firestore!, album.id);
        if (mounted) {
          setFallbackSubAlbums(albums);
        }
      } catch (err) {
        console.error('Error loading sub-albums:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchSubAlbums();
    return () => { mounted = false; };
  }, [firestore, album.id, expanded, searchMode, album.children, fallbackSubAlbums.length]);

  const hasSubAlbums = !searchMode && (effectiveChildren.length > 0 || (loading)); // show chevron if loading or has children

  const handleClick = (e: React.MouseEvent) => {
    if (onAlbumSelect && !useLinks) {
      e.preventDefault();
      onAlbumSelect(album);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const AlbumIcon = getAlbumIcon(album, expanded);

  const content = (
    <div
      className={cn(
        'flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-accent transition-colors',
        isSelected && 'bg-accent font-medium',
        depth > 0 && 'ml-6'
      )}
      onClick={handleClick}
    >
      {/* Expand/collapse button */}
      {(hasSubAlbums || (!album.children && !loading)) && (
        <button
          onClick={handleToggle}
          className={cn(
            "p-0.5 hover:bg-accent-foreground/10 rounded transition-colors",
            (!hasSubAlbums && !loading) && "invisible" // Hide chevron if truly leaf
          )}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      )}

      {/* Icon */}
      <AlbumIcon
        className={cn(
          'w-4 h-4',
          album.albumType === 'member-personal' && 'text-blue-500',
          album.albumType === 'field-flights' && 'text-green-500',
          album.albumType === 'system' && 'text-purple-500'
          // removed photonlink style
        )}
      />

      {/* Album name */}
      <span className="flex-1 text-sm truncate" title={album.name}>{album.name}</span>

      {/* Media count badge */}
      {showMediaCounts && album.mediaCount > 0 && (
        <Badge variant="secondary" className="text-xs px-1.5 py-0 scale-90">
          {album.mediaCount}
        </Badge>
      )}
    </div>
  );

  return (
    <div>
      {useLinks ? (
        <Link href={`${baseLinkUrl}/${album.id}?source=${album.source}`}>
          {content}
        </Link>
      ) : (
        content
      )}

      {/* Render sub-albums */}
      {expanded && effectiveChildren.length > 0 && (
        <div className="ml-2">
          {effectiveChildren.map((subAlbum) => (
            <AlbumTreeNode
              key={subAlbum.id}
              album={subAlbum}
              depth={depth + 1}
              autoExpandDepth={autoExpandDepth}
              selectedAlbumId={selectedAlbumId}
              onAlbumSelect={onAlbumSelect}
              showMediaCounts={showMediaCounts}
              useLinks={useLinks}
              baseLinkUrl={baseLinkUrl}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Get appropriate icon for album type
 */
function getAlbumIcon(album: HybridAlbum, expanded: boolean) {
  switch (album.albumType) {
    case 'member-personal':
      return Users;
    case 'field-flights':
      return Plane;
    case 'system':
      return expanded ? FolderOpen : Folder;
    default:
      return expanded ? FolderOpen : Folder;
  }
}

/**
 * Loading skeleton for album tree
 */
function AlbumTreeSkeleton() {
  return (
    <div className="space-y-2 p-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <Skeleton className="w-4 h-4" />
          <Skeleton className="w-4 h-4" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="w-8 h-4" />
        </div>
      ))}
    </div>
  );
}
